import React, { useState, useEffect } from 'react';
import { CustomDialog } from '../../Component/Dialog';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import Button from '@mui/material/Button';
import PropTypes from 'prop-types';
import useProjectStore from '../../ProjectCanvas/useProjectStore';
import { send_request } from '../../../utils/Request';
import { LEGACY_FORM_TYPES } from '../../../utils/formTypeConstants';
import { loadDraftProject } from '../../../utils/canvasApi';
import FormBuilderContainer from '../FormBuilderContainer';
import SequentialFormContainer from '../../SequentialForm/SequentialFormContainer';

import { v4 as uuid } from 'uuid';

export const CopyToFormDialog = props => {
  const {
    open,
    onClose,
    questionId,
    currentFormId,
    onSuccess,
    containerType = 'form', // 'form' or 'sequential'
  } = props;

  // Get the correct container based on form type
  const getContainer = () => {
    return containerType === 'sequential' ? SequentialFormContainer : FormBuilderContainer;
  };

  const [selectedFormId, setSelectedFormId] = useState('');
  const [availableForms, setAvailableForms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copying, setCopying] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (open) {
      loadAvailableForms();
      setSelectedFormId('');
      setError('');
      setSuccess(false);
    }
  }, [open]);

  const loadAvailableForms = async () => {
    setLoading(true);
    const formTypes = LEGACY_FORM_TYPES;

    try {
      // Fetch fresh component data from the API to get the latest state
      const projectId = useProjectStore.getState().project_id;
      const res = await loadDraftProject(projectId);

      if (res && res.data && res.data.components) {
        const components = res.data.components;

        // Filter to only include form-type components, excluding the current form
        const forms = components
          .filter(comp => formTypes.includes(comp.type) && comp.componentId !== currentFormId)
          .map(comp => ({
            componentId: comp.componentId,
            name: comp.componentData?.name || comp.generatedTitle || 'Untitled Form',
            type: comp.type,
          }));

        setAvailableForms(forms);
      } else {
        // Fallback to local store if API fails
        const forms = useProjectStore.getState().components
          .filter(comp => formTypes.includes(comp.type) && comp.componentId !== currentFormId)
          .map(comp => ({
            componentId: comp.componentId,
            name: comp.componentData?.name || comp.generatedTitle || 'Untitled Form',
            type: comp.type,
          }));
        setAvailableForms(forms);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      const forms = useProjectStore.getState().components
        .filter(comp => formTypes.includes(comp.type) && comp.componentId !== currentFormId)
        .map(comp => ({
          componentId: comp.componentId,
          name: comp.componentData?.name || comp.generatedTitle || 'Untitled Form',
          type: comp.type,
          }));
        setAvailableForms(forms);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!selectedFormId) {
      setError('Please select a form to copy the question to.');
      return;
    }

    setCopying(true);
    setError('');

    try {
      // Get the question data to be copied using the correct container
      const container = getContainer();
      const questionData = container.getData(questionId);
      if (!questionData) {
        setError('Could not find the question to copy.');
        setCopying(false);
        return;
      }

      // Fetch the target form's current data
      let version = useProjectStore.getState().production
        ? useProjectStore.getState().publishedVersion
        : useProjectStore.getState().draftVersion;

      const response = await send_request(
        `project-service/project/component/query/${selectedFormId}/at-version/${version}`,
        '',
        {}
      );

      if (!response.data || !response.data.components || !response.data.components[0]) {
        setError('Could not load the target form data.');
        setCopying(false);
        return;
      }

      const targetFormData = response.data.components[0].componentData;
      const targetFormPages = targetFormData?.data?.pages || {
        0: {
          title: {
            view: { format: '', data: { default: 'Page Title', dynamic: '' } },
            style: '',
            description: { default: '', dynamic: '' },
          },
          components: [],
        },
      };
      const targetFormLength = Object.keys(targetFormPages).length || 1;

      // Create a copy of the question with new ID and key
      const copiedQuestion = createQuestionCopy(questionData);

      // Find the last page and add the question to it
      const lastPageIndex = targetFormLength - 1;
      if (!targetFormPages[lastPageIndex]) {
        targetFormPages[lastPageIndex] = {
          title: {
            view: { format: '', data: { default: 'Page Title', dynamic: '' } },
            style: '',
            description: { default: '', dynamic: '' },
          },
          components: [],
        };
      }
      targetFormPages[lastPageIndex].components.push(copiedQuestion);

      // Prepare the save data
      const saveData = {
        componentData: {
          data: {
            ...targetFormData.data,
            pages: targetFormPages,
          },
          name: targetFormData.name,
          lastModified: new Date().toISOString(),
        },
        componentId: selectedFormId,
        type: response.data.components[0].type || 'form',
      };

      // Save the updated form
      await send_request('component-service/form/data', saveData, '', 'POST');

      setSuccess(true);
      if (onSuccess) {
        onSuccess();
      }

      // Close dialog after short delay to show success
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error copying question:', err);
      setError('Failed to copy the question. Please try again.');
    } finally {
      setCopying(false);
    }
  };

  const createQuestionCopy = questionData => {
    // Convert the FormBuilderContainer format to the save format
    const format = FormBuilderContainer.convertFormat(questionData.type);
    const config = questionData.config;

    return {
      key:
        questionData.text.replace(/ /g, '') +
        (Math.floor(Math.random() * 90000000) + 10000).toString(),
      required: config.required,
      comment: config.comment,
      visibility: config.visibility,
      read_only: config.readonly,
      auto: config.autoAnswer,
      key_active_auto: getKeyButtonGroup(config.dataAuto),
      key_visibility: getKeyButtonGroup(config.dataVisibility),
      key_readonly: getKeyButtonGroup(config.dataReadOnly),
      key_required: getKeyButtonGroup(config.dataRequired),
      key_comment: getKeyButtonGroup(config.dataComment),
      notificationEmails: config.notificationEmails,
      optionsSource: config.optionsSource,
      size: config.size,
      height: config.height,
      width: config.width,
      embedType: config.embedType,
      url: config.url,
      snippet: config.snippet,
      numRows: config.numRows,
      exportType: config.exportType,
      exportFormat: config.exportFormat,
      columns: config.columns,
      tableCells: config.tableCells,
      minItems: config.minItems,
      maxItems: config.maxItems,
      drop_down: config.drop_down ? config.drop_down.toString() : '',
      outputFormat: config.outputFormat,
      databaseId: config.databaseId,
      columnId: config.columnId,
      labelColumnId: config.labelColumnId,
      valueColumnId: config.valueColumnId,
      timezone: config.timezone,
      file_type: config.file_type,
      sourceMode: config.sourceMode,
      presetImageUrl: config.presetImageUrl,
      addressFields: config.addressFields,
      defaultContent: config.defaultContent,
      validation: {
        min: config.validation?.min,
        max: config.validation?.max,
        message: config.validation?.message,
        regex: config.validation?.regex,
      },
      views: {
        question: {
          view: {
            format: 'row_text',
            data: {
              default: config.question_text_default,
              dynamic: '',
            },
          },
          style: '',
          description: {
            default: config.question_description_default,
            dynamic: '',
          },
        },
        answer: {
          view: {
            format: format,
            data: {
              rowLabels: config.rowLabels,
              default:
                questionData.type === 4 || questionData.type === 5 || questionData.type === 10
                  ? config.answer_list
                  : questionData.type === 15 || questionData.type === 19
                  ? config.columns
                  : questionData.type === 17
                  ? config.defaultContent
                  : config.answer_data_default,
              dynamic: '',
            },
          },
          style: '',
          description: {
            default: '',
            dynamic: '',
          },
        },
      },
    };
  };

  const getKeyButtonGroup = data => {
    if (data) {
      const obj = data.filter(ele => ele.active === true);
      if (obj && obj[0]) {
        return obj[0].key;
      }
    }
    return '';
  };

  const getFormTypeLabel = type => {
    switch (type) {
      case 'form':
        return 'Form';
      case 'sequential_form':
        return 'Assign Task';
      case 'form_section':
        return 'Form Section';
      default:
        return 'Form';
    }
  };

  return (
    <CustomDialog
      isOpen={open}
      handleClose={onClose}
      title={'Copy question to another form'}
      contents={
        <>
          <p style={{ marginBottom: '16px' }}>
            Select the form you want to copy this question to. The question will be added to the end
            of the selected form.
          </p>

          {error && (
            <Alert severity="error" style={{ marginBottom: '16px' }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" style={{ marginBottom: '16px' }}>
              Question copied successfully!
            </Alert>
          )}

          {loading ? (
            <Grid container justifyContent="center" style={{ padding: '20px' }}>
              <CircularProgress size={24} />
            </Grid>
          ) : availableForms.length === 0 ? (
            <Alert severity="info">
              No other forms available in this workflow to copy the question to.
            </Alert>
          ) : (
            <FormControl fullWidth variant="outlined">
              <InputLabel id="target-form-label">Target Form</InputLabel>
              <Select
                labelId="target-form-label"
                id="target-form-select"
                value={selectedFormId}
                onChange={e => setSelectedFormId(e.target.value)}
                label="Target Form"
                disabled={copying}
              >
                {availableForms.map(form => (
                  <MenuItem key={form.componentId} value={form.componentId}>
                    {form.name} ({getFormTypeLabel(form.type)})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      }
      buttons={
        <>
          <Button onClick={onClose} variant={'outlined'} color={'info'} disabled={copying}>
            Cancel
          </Button>
          <Button
            onClick={handleCopy}
            variant={'contained'}
            color={'primary'}
            disabled={copying || loading || availableForms.length === 0 || !selectedFormId}
          >
            {copying ? (
              <>
                Copying...
                <CircularProgress size={16} style={{ marginLeft: '8px', color: 'white' }} />
              </>
            ) : (
              'Copy'
            )}
          </Button>
        </>
      }
    />
  );
};

CopyToFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  questionId: PropTypes.string,
  currentFormId: PropTypes.string.isRequired,
  onSuccess: PropTypes.func,
  containerType: PropTypes.oneOf(['form', 'sequential']),
};

export default CopyToFormDialog;
